import type { ComponentType } from 'react';

// The registry holds wrapper components whose props types have nothing in
// common, so there is no one precise type covering them. `JSONProps<any>` is
// not the substitute it looks like: TS resolves `keyof any` to
// `string | number | symbol`, making it a real structural type that rejects
// every wrapper's own props. `any` is what leaves the props unchecked here,
// where each wrapper checks its own.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- a registry of components whose props types have nothing in common
type LazyComponent = Promise<ComponentType<any>>;

export async function ProfileEditRichTextEditor(): LazyComponent {
  return (
    await import(
      'profiles/common/edit/components/ProfileEditRichTextEditor.rails'
    )
  ).ProfileEditRichTextEditorWrapper;
}

export async function LandingPageRichTextEditor(): LazyComponent {
  return (
    await import(
      'profiles/common/edit/components/LandingPageRichTextEditor.rails'
    )
  ).LandingPageRichTextEditorWrapper;
}

export async function AvailabilitySourceForm(): LazyComponent {
  return (await import('reservations/components/AvailabilitySourceForm'))
    .AvailabilitySourceForm;
}

export async function PhoneNumberForm(): LazyComponent {
  return (await import('phoneNumbers/components/PhoneNumberForm')).default;
}

export async function VenueMobileCTAs(): LazyComponent {
  return (await import('phoneNumbers/VenueMobileCTAs.rails'))
    .VenueMobileCTAsWrapper;
}

export async function Rating(): LazyComponent {
  return (await import('common/components/Rating')).Rating;
}

export async function RatingBoxAccordion(): LazyComponent {
  return (await import('common/components/accordion/RatingBoxAccordion'))
    .RatingBoxAccordion;
}
export async function StarRating(): LazyComponent {
  return (await import('common/components/StarRating/StarRating.rails'))
    .StarRatingWrapper;
}

export async function AccordionPanel(): LazyComponent {
  return (await import('common/components/accordion/AccordionPanel'))
    .AccordionPanel;
}

export async function CommissionRoot(): LazyComponent {
  return (await import('commission/CommissionRoot.rails'))
    .CommissionRootWrapper;
}

export async function PackageEditorRoot(): LazyComponent {
  return (await import('packages/edit/components/PackageEditorRoot.rails'))
    .PackageEditorRootWrapper;
}

export async function PackageOrderRoot(): LazyComponent {
  return (await import('packages/order/components/PackageOrderRoot.rails'))
    .PackageOrderRootWrapper;
}

export async function VenueInquiryConversationRoot(): LazyComponent {
  return (
    await import(
      'conversations/show/components/venueInquiry/conversation/ConversationRoot.rails'
    )
  ).ConversationRootWrapper;
}

export async function CateringInquiryConversationApp(): LazyComponent {
  return (
    await import(
      'conversations/show/components/cateringInquiry/ConversationApp.rails'
    )
  ).ConversationWrapper;
}

export async function ReadOnlyVenueInquiryConversationApp(): LazyComponent {
  return (
    await import(
      'conversations/show/components/venueInquiry/readOnly/ConversationApp.rails'
    )
  ).ConversationWrapper;
}

export async function MarkAsRepliedRoot(): LazyComponent {
  return (
    await import(
      'conversations/show/components/venueInquiry/markAsReplied/MarkAsRepliedRoot.rails'
    )
  ).MarkAsRepliedRootWrapper;
}

export async function VenueGroupEditorApp(): LazyComponent {
  return (await import('groups/venues/edit/VenueGroupEditorApp.rails'))
    .VenueGroupEditorAppWrapper;
}

export async function SendCopy(): LazyComponent {
  return (await import('thanks/SendCopy.rails')).SendCopyWrapper;
}

export async function MessageTemplateEditorApp(): LazyComponent {
  return (
    await import('messageTemplates/components/MessageTemplateEditorApp.rails')
  ).MessageTemplateEditorAppWrapper;
}

export async function MultiSelect(): LazyComponent {
  return (await import('common/components/MultiSelect/MultiSelect.rails'))
    .default;
}

export async function VenuePageInquiryFormNew(): LazyComponent {
  return (await import('inquiry/components/VenuePageInquiryForm.rails'))
    .VenuePageInquiryFormWrapper;
}

export async function MultiVenueInquiryForm(): LazyComponent {
  return (await import('inquiry/components/MultiVenueInquiryForm.rails'))
    .MultiVenueInquiryFormWrapper;
}

export async function SearchableInquiryIndex(): LazyComponent {
  return (
    await import('conversations/index/components/SearchableInquiryIndex.rails')
  ).SearchableInquiryIndexWrapper;
}

export async function FeaturedEventTypeField(): LazyComponent {
  return (
    await import('staff/featuredVenues/components/FeaturedEventTypeField')
  ).FeaturedEventTypeField;
}

export async function FeaturedVenuesInventory(): LazyComponent {
  return (await import('staff/featuredVenues/FeaturedVenuesInventory.rails'))
    .FeaturedVenuesInventoryWrapper;
}

export async function EditOrganizationTabs(): LazyComponent {
  return (await import('staff/organization/components/EditOrganizationTabs'))
    .default;
}

export async function SpamInquiriesTabs(): LazyComponent {
  return (await import('spamInquiries/staff/components/SpamInquiriesTabs'))
    .default;
}

export async function EditVenueServices(): LazyComponent {
  return (await import('staff/organization/components/EditVenueServices'))
    .EditVenueServices;
}

export async function EditVenueTypes(): LazyComponent {
  return (await import('staff/organization/components/EditVenueTypes'))
    .EditVenueTypes;
}

export async function ProfileAnnouncementsEditorApp(): LazyComponent {
  return (
    await import('staff/organization/ProfileAnnouncementsEditorApp.rails')
  ).ProfileAnnouncementsEditorAppWrapper;
}

export async function CateringSearch(): LazyComponent {
  return (await import('catering/search/components/CateringSearch.rails'))
    .CateringSearchWrapper;
}

// ============================================= START PROFILE PAGE RELATED COMPONENTS

export async function VenueProfileGallery(): LazyComponent {
  return (
    await import('profiles/venues/show/components/VenueProfileGallery.rails')
  ).VenueProfileGalleryWrapper;
}

export async function VenueWishlistButton(): LazyComponent {
  return (
    await import(
      'profiles/venues/show/components/venueProfileGallery/VenueWishlistButton.rails'
    )
  ).VenueWishlistButtonWrapper;
}

export async function VenueProfileLocation(): LazyComponent {
  return (
    await import('profiles/venues/show/components/VenueProfileLocation.rails')
  ).VenueProfileLocationWrapper;
}

export async function VenueProfileVideoSection(): LazyComponent {
  return (
    await import(
      'profiles/venues/show/components/VenueProfileVideoSection.rails'
    )
  ).VenueProfileVideoSectionWrapper;
}

export async function VenueProfileVirtualTourSection(): LazyComponent {
  return (
    await import(
      'profiles/venues/show/components/VenueProfileVirtualTourSection.rails'
    )
  ).VenueProfileVirtualTourSectionWrapper;
}

export async function VenueProfileStoriesOfPastEvents(): LazyComponent {
  return (
    await import(
      'profiles/venues/show/components/VenueProfileStoriesOfPastEvents.rails'
    )
  ).VenueProfileStoriesOfPastEventsWrapper;
}

export async function VenueProfileCapacityDetails(): LazyComponent {
  return (
    await import(
      'profiles/venues/show/components/VenueProfileCapacityDetails.rails'
    )
  ).VenueProfileCapacityDetailsWrapper;
}

export async function VenueProfileCTASection(): LazyComponent {
  return (
    await import('profiles/venues/show/components/VenueProfileCTASection.rails')
  ).VenueProfileCTASectionWrapper;
}

export async function VenueProfileOtherSpacesInSameLocation(): LazyComponent {
  return (
    await import(
      'profiles/venues/show/components/VenueProfileOtherSpacesInSameLocation.rails'
    )
  ).VenueProfileOtherSpacesInSameLocationWrapper;
}

export async function VenueProfileReviewList(): LazyComponent {
  return (
    await import('profiles/venues/show/components/VenueProfileReviewList.rails')
  ).VenueProfileReviewListWrapper;
}

export async function VenueProfileServicesAndFacilities(): LazyComponent {
  return (
    await import(
      'profiles/venues/show/components/VenueProfileServicesAndFacilities.rails'
    )
  ).VenueProfileServicesAndFacilitiesWrapper;
}

export async function VenueProfileSimilarVenues(): LazyComponent {
  return (
    await import(
      'profiles/venues/show/components/VenueProfileSimilarVenues.rails'
    )
  ).VenueProfileSimilarVenuesWrapper;
}

export async function VenueProfilePricingSection(): LazyComponent {
  return (
    await import(
      'profiles/venues/show/components/VenueProfilePricingSection.rails'
    )
  ).VenueProfilePricingSectionWrapper;
}

export async function VenueProfileAvailabilitySection(): LazyComponent {
  return (
    await import(
      'profiles/venues/show/components/VenueProfileAvailabilitySection.rails'
    )
  ).VenueProfileAvailabilitySectionWrapper;
}

export async function VenueProfileBasicInformation(): LazyComponent {
  return (
    await import(
      'profiles/venues/show/components/VenueProfileBasicInformation.rails'
    )
  ).VenueProfileBasicInformationWrapper;
}

export async function VenueProfileTranslatedDescription(): LazyComponent {
  return (
    await import(
      'profiles/venues/show/components/VenueProfileTranslatedDescription.rails'
    )
  ).VenueProfileTranslatedDescriptionWrapper;
}

export async function CateringBasicInformation(): LazyComponent {
  return (await import('profiles/catering/show/cateringComponents.rails'))
    .CateringBasicInformation;
}
export async function CateringDetailedInformation(): LazyComponent {
  return (await import('profiles/catering/show/cateringComponents.rails'))
    .CateringDetailedInformation;
}
export async function CateringHeading(): LazyComponent {
  return (await import('profiles/catering/show/cateringComponents.rails'))
    .CateringHeading;
}
export async function ProfileImageCarousel(): LazyComponent {
  return (
    await import('profiles/common/show/components/ProfileImageCarousel.rails')
  ).ProfileImageCarouselWrapper;
}
export async function CateringInquiry(): LazyComponent {
  return (await import('profiles/catering/show/cateringComponents.rails'))
    .CateringInquiry;
}
export async function CateringServicesAndPricing(): LazyComponent {
  return (await import('profiles/catering/show/cateringComponents.rails'))
    .CateringServicesAndPricing;
}
export async function CateringMenuList(): LazyComponent {
  return (await import('profiles/catering/show/cateringComponents.rails'))
    .CateringMenuList;
}

export async function SupplierProfileServicesAndPricing(): LazyComponent {
  return (
    await import(
      'profiles/suppliers/show/components/SupplierProfileServicesAndPricing.rails'
    )
  ).SupplierProfileServicesAndPricingWrapper;
}
export async function SupplierProfileHeading(): LazyComponent {
  return (
    await import(
      'profiles/suppliers/show/components/SupplierProfileHeading.rails'
    )
  ).SupplierProfileHeadingWrapper;
}
export async function SupplierProfileContactInformation(): LazyComponent {
  return (
    await import(
      'profiles/suppliers/show/components/SupplierProfileContactInformation.rails'
    )
  ).SupplierProfileContactInformationWrapper;
}
export async function SupplierProfileVideo(): LazyComponent {
  return (
    await import(
      'profiles/suppliers/show/components/SupplierProfileVideo.rails'
    )
  ).SupplierProfileVideoWrapper;
}
export async function SupplierProfileInquiry(): LazyComponent {
  return (
    await import(
      'profiles/suppliers/show/components/SupplierProfileInquiry.rails'
    )
  ).InquiryWrapper;
}
export async function SupplierProfileContactImage(): LazyComponent {
  return (
    await import(
      'profiles/suppliers/show/components/SupplierProfileContactImage.rails'
    )
  ).SupplierProfileContactImageWrapper;
}
export async function SupplierProfileBasicInformation(): LazyComponent {
  return (
    await import(
      'profiles/suppliers/show/components/SupplierProfileBasicInformation.rails'
    )
  ).SupplierProfileBasicInformationWrapper;
}

// ============================================= END PROFILE PAGE RELATED COMPONENTS

export async function SupplierSearch(): LazyComponent {
  return (
    await import('profiles/suppliers/search/components/SupplierSearch.rails')
  ).SupplierSearchWrapper;
}

export async function LegalNotice(): LazyComponent {
  return (await import('common/components/inquiry/legalNotice/LegalNotice'))
    .LegalNotice;
}

export async function PipedriveIntegrationStatus(): LazyComponent {
  return (await import('staff/organization/PipedriveIntegrationStatus.rails'))
    .PipedriveIntegrationStatusWrapper;
}

export async function BillingDetails(): LazyComponent {
  return (
    await import('billing/admin/billingDetails/components/BillingDetails.rails')
  ).BillingDetailsWrapper;
}
export async function TinField(): LazyComponent {
  return (await import('billing/admin/tin/components/TinField.rails'))
    .TinFieldWrapper;
}
export async function InvoicesOfDayStates(): LazyComponent {
  return (
    await import(
      'billing/staff/dailyInvoices/components/InvoicesOfDayStates.rails'
    )
  ).InvoicesOfDayStatesWrapper;
}
export async function InvoicingFields(): LazyComponent {
  return (
    await import(
      'billing/admin/invoicingFields/components/InvoicingFields.rails'
    )
  ).InvoicingFieldsWrapper;
}
export async function OrganizationInvoices(): LazyComponent {
  return (
    await import(
      'billing/staff/organizationInvoices/components/OrganizationInvoices.rails'
    )
  ).OrganizationInvoicesWrapper;
}
export async function StripeCustomerStatus(): LazyComponent {
  return (
    await import(
      'billing/staff/stripeCustomerStatus/components/StripeCustomerStatus.rails'
    )
  ).StripeCustomerStatusWrapper;
}

export async function OutcomeFeedback(): LazyComponent {
  return (await import('outcomes/thanks/OutcomeFeedback.rails'))
    .OutcomeFeedbackWrapper;
}

export async function EditOpeningHours(): LazyComponent {
  return (
    await import('profiles/common/edit/components/EditOpeningHours.rails')
  ).EditOpeningHoursWrapper;
}

export async function InquiryRecipientPersonaSelect(): LazyComponent {
  return (
    await import(
      'profiles/common/edit/components/InquiryRecipientPersonaSelect.rails'
    )
  ).InquiryRecipientPersonaSelectWrapper;
}
